import { isDefined } from "@clipboard-health/util-ts";
import { Chip, Stack } from "@mui/material";
import { isPast } from "date-fns";

import { useSession } from "../Auth/api/useSession";
import { useGetShiftTimeProposalById } from "../ShiftTimeProposals/api/useShiftTimeProposals";
import { PACIFIC_TIME_ZONE } from "../Timezone/constants";
import { ChatWidgetCard } from "./ChatWidgetCard";
import {
  EXPIRED_SHIFT_TIME_PROPOSAL_CHIP,
  formatShiftStartEndDateTime,
  SHIFT_TIME_PROPOSAL_STATUS_CHIP_MAP,
} from "./utils";

interface ShiftTimeProposalRequestMessageProps {
  shiftTimeProposalId: string;
  originalStartTime: string;
  originalEndTime: string;
}

export function ShiftTimeProposalRequestMessage(props: ShiftTimeProposalRequestMessageProps) {
  const { shiftTimeProposalId, originalStartTime, originalEndTime } = props;

  const { profile } = useSession();
  const { data: shiftTimeProposalResponse } = useGetShiftTimeProposalById(
    { shiftTimeProposalId },
    { enabled: isDefined(shiftTimeProposalId) }
  );

  const shiftTimeProposal = shiftTimeProposalResponse?.data;

  if (!isDefined(shiftTimeProposal)) {
    // If there is no shift time proposal to show, then render an empty message
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  const { status, proposedStartTime, proposedEndTime } = shiftTimeProposal.attributes;
  const timeZone = profile?.tmz ?? PACIFIC_TIME_ZONE;

  const proposedShiftStartEnd = formatShiftStartEndDateTime(
    proposedStartTime,
    proposedEndTime,
    timeZone
  );

  const originalShiftStartEnd = formatShiftStartEndDateTime(
    originalStartTime,
    originalEndTime,
    timeZone
  );

  const isExpired = isPast(new Date(proposedStartTime));

  const chipProps = isExpired
    ? EXPIRED_SHIFT_TIME_PROPOSAL_CHIP
    : SHIFT_TIME_PROPOSAL_STATUS_CHIP_MAP[status];

  return (
    <Stack alignItems="flex-end" className="sendbird-admin-message">
      <ChatWidgetCard
        statusChip={
          <Chip
            label={chipProps.label}
            aria-label={chipProps.label}
            color={chipProps.color}
            variant="outlined"
            sx={{
              border: "none",
              borderRadius: 2,
              fontWeight: "bold",
              ...chipProps.customStyles,
            }}
          />
        }
        title="You proposed a new shift time"
        subtitle={proposedShiftStartEnd}
        caption={`Previous: ${originalShiftStartEnd}`}
      />
    </Stack>
  );
}
